/**
 * SafetySupportCard · v2.6.9 batch BK
 * ====================================
 * Vertically-stacked support contact card shared by Driver + Rider dashboards.
 * Designed to sit directly below the Emergency / Panic button group so all
 * three classes of help-seeking (911 panic → dispatch line → email support)
 * are co-located.
 *
 * Spec colors (per ticket):
 *   background  · #121212
 *   text        · #E0E0E0
 *   accent      · #FF0000 (emergency labels only — used sparingly to preserve impact)
 *
 * Rows:
 *   1. Contact Dispatch  · tel:(580) 219-4687
 *   2. Rider Support     · mailto:support@bullertaxi.com
 *   3. Safety Concerns   · mailto:safety@bullertaxi.com
 *
 * Each row has ≥16px padding to prevent fat-finger errors and uses the device's
 * native dialer / email client via `tel:` / `mailto:` URI handlers.
 */
import { Card } from "@/components/ui/card";
import { Phone, Mail, ShieldAlert, LifeBuoy } from "lucide-react";

// Hard-coded contact endpoints. Centralized here so updates in one place
// flow to both driver + rider views.
// v2.7.65+E148 · Phase 1 · Central dispatch number updated to
// (580) 219-4687. If you need to change it again, this constant is the
// single source of truth for both apps' Safety & Support drawer.
const CONTACTS = [
  {
    label: "Contact Dispatch",
    value: "(580) 219-4687",
    href: "tel:+15802194687",
    Icon: Phone,
    testid: "support-contact-dispatch",
    accent: "amber",
    description: "24/7 live dispatch line",
  },
  {
    label: "Rider Support",
    value: "support@bullertaxi.com",
    href: "mailto:support@bullertaxi.com",
    Icon: LifeBuoy,
    testid: "support-rider-support",
    accent: "indigo",
    description: "Lost items · trip questions · billing",
  },
  {
    label: "Safety Concerns",
    value: "safety@bullertaxi.com",
    href: "mailto:safety@bullertaxi.com",
    Icon: ShieldAlert,
    testid: "support-safety-concerns",
    accent: "red",
    description: "Report unsafe driving · misconduct · incidents",
  },
];

function accentRing(accent) {
  if (accent === "red") return "border-red-500/50 hover:bg-red-500/10 hover:border-red-400";
  if (accent === "amber") return "border-amber-500/50 hover:bg-amber-500/10 hover:border-amber-400";
  if (accent === "indigo") return "border-indigo-500/50 hover:bg-indigo-500/10 hover:border-indigo-400";
  return "border-zinc-700 hover:bg-zinc-800";
}
function accentIcon(accent) {
  if (accent === "red") return "text-red-400";
  if (accent === "amber") return "text-amber-300";
  if (accent === "indigo") return "text-indigo-300";
  return "text-zinc-400";
}
function accentValue(accent) {
  if (accent === "red") return "text-red-300";
  if (accent === "amber") return "text-amber-300";
  if (accent === "indigo") return "text-indigo-300";
  return "text-zinc-200";
}

/**
 * @param {object} props
 * @param {string} [props.title]      defaults to "Safety & Support"
 * @param {boolean} [props.compact]   tighter padding for embedded contexts
 * @param {string} [props.testid]     wrapper testid · default `safety-support-card`
 */
export default function SafetySupportCard({ title = "Safety & Support", compact = false, testid = "safety-support-card" }) {
  return (
    <Card
      data-testid={testid}
      className="border-zinc-800 p-4 sm:p-5 space-y-3"
      style={{ backgroundColor: "#121212", color: "#E0E0E0" }}
    >
      <div className="flex items-center gap-2">
        <ShieldAlert className="h-4 w-4" style={{ color: "#FF0000" }}/>
        <h3 className="font-bold text-sm sm:text-base uppercase tracking-wider" style={{ color: "#E0E0E0" }}>{title}</h3>
      </div>
      <p className="text-[11px] sm:text-xs" style={{ color: "#A0A0A0" }}>
        Tap any line to launch your phone's dialer or email app.
      </p>
      <ul className={`space-y-${compact ? "1.5" : "2"}`} data-testid="safety-support-list">
        {CONTACTS.map((c) => {
          const Icon = c.Icon;
          return (
            <li key={c.testid}>
              <a
                href={c.href}
                data-testid={c.testid}
                target={c.href.startsWith("tel:") ? undefined : "_blank"}
                rel={c.href.startsWith("tel:") ? undefined : "noreferrer"}
                className={`flex items-center gap-3 rounded-md border-2 px-3.5 py-3.5 transition-colors ${accentRing(c.accent)}`}
                style={{ backgroundColor: "#1A1A1A", minHeight: 56 }}
              >
                <div className={`h-9 w-9 rounded-md flex items-center justify-center flex-shrink-0 ${accentIcon(c.accent)}`} style={{ backgroundColor: "#0A0A0A" }}>
                  <Icon className="h-4 w-4"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: "#A0A0A0" }}>{c.label}</div>
                  <div className={`font-extrabold truncate ${accentValue(c.accent)}`} style={{ fontSize: 15 }}>{c.value}</div>
                  <div className="text-[10px] mt-0.5" style={{ color: "#707070" }}>{c.description}</div>
                </div>
              </a>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
